/**
 * @fileoverview 乗下船表（乗組員配置管理のリニューアル）
 * @description 従来Excelの乗下船表をそのままシステム化した画面。
 *   - 乗/休グリッド: 配乗記録から自動描画（乗船日▲・下船日▼、希望休オーバーレイ）
 *   - 右側: 月間乗/休日数 ＋ 年度累計（起算日は会社ごと設定・年度リセット）
 *   - 下部: 日別の乗船/休人数、乗下船イベント（旧メモ書きの構造化）
 *   - イベント登録で発令予約が自動生成され、乗組員配置・勤怠と常に一致する
 */
import { Link } from 'react-router-dom';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as api from '../services/api';
import { getToken, getApiBase } from '../services/api';
import BoardingExcelImport from '../components/BoardingExcelImport';
import type { BoardingExcelImportHandle } from '../components/BoardingExcelImport';
import ExcelMenu from '../components/ExcelMenu';
import { hasFeature } from '../utils/featureFlags';
import {
  Ship, ChevronLeft, ChevronRight, Plus, X, Download, Settings2,
  CalendarDays, Trash2, Anchor, RefreshCw, FileDown, FileUp, AlertTriangle,
  Eye, EyeOff,
} from 'lucide-react';

/* ══════════ 型 ══════════ */
interface DayCell { s: 'on' | 'off' | 'none'; b?: 1; d?: 1; w?: 'pending' | 'approved'; o?: string; oi?: string; dp?: string }
interface DutyPeriod { id: string; duty_position: string; start_date: string; end_date: string | null; note: string | null }
interface CrewRow {
  user_id: string; user_name: string; employee_code: string;
  avatar_path: string | null; positions: string[];
  /** 辞令上の役職（ユーザー管理で設定）。乗船中に執る職＝positions とは別 */
  rank_position: string | null;
  duties: DutyPeriod[];
  group_vessel_id: string | null; group_vessel_name: string | null;
  days: DayCell[];
  month_on: number; month_off: number; cum_on: number; cum_off: number;
}
interface ManningRule { role_name: string; min_count: number }
interface ManningIssue { role: string; count: number; min: number }
interface EventItem { id?: string; user_id: string; user_name?: string; action: 'board' | 'disembark'; position?: string | null; note?: string | null }
interface ChangeEvent {
  id: string; event_date: string; end_date: string | null; event_type: string;
  port_name: string | null; berth_note: string | null; note: string | null;
  items: EventItem[];
}
interface SheetData {
  month: string; n_days: number; year_start: string; fiscal_from: string;
  crew: CrewRow[]; daily_on: number[]; daily_off: number[];
  events: ChangeEvent[];
  manning_rules: ManningRule[]; daily_manning: ManningIssue[][];
  dock_days?: boolean[];
  rests: Array<{ user_id: string; start_date: string; end_date: string; status: string; request_type: string }>;
}
interface Vessel { id: string; vessel_name: string }
interface UserLite { id: string; user_name: string; user_role: string; is_crew_eligible?: number }

const EVENT_TYPE = {
  change: { label: '交代',           color: '#0E5AA7', bg: '#E8F1FB' },
  berth:  { label: '仮バース・停泊', color: '#7C3AED', bg: '#F3EFFD' },
  dock:   { label: 'ドック・入渠',   color: '#B45309', bg: '#FDF3E3' },
  other:  { label: 'その他',         color: '#64748B', bg: '#F1F5F9' },
} as const;

/** 執職（役職）の系統色。乗下船表・執職日数で共通に使う */
export function positionColor(p: string): { bg: string; fg: string } {
  if (/船長/.test(p)) return { bg: '#FCE9E7', fg: '#B42318' };
  if (/航海|航士|副船長/.test(p)) return { bg: '#E8F1FB', fg: '#0E5AA7' };
  if (/甲板/.test(p)) return { bg: '#E0F2FE', fg: '#0369A1' };
  if (/機関長/.test(p)) return { bg: '#DCFCE7', fg: '#15803D' };
  if (/機関|一機|二機|三機/.test(p)) return { bg: '#E4F0E7', fg: '#166534' };
  if (/司厨/.test(p)) return { bg: '#FBEEDD', fg: '#92600A' };
  return { bg: '#F1F5F9', fg: '#5A6B7E' };
}

/** 役職マスタが未整備の場合のフォールバック（通常は /position-masters から取得） */
const POSITIONS_FALLBACK = ['船長', '一等航海士', '二等航海士', '三等航海士', '甲板長', '甲板員', '機関長', '一等機関士', '二等機関士', '機関員', '司厨長'];

function ymNow(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function ymAdd(ym: string, n: number): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y!, m! - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}
function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const YOUBI = ['日', '月', '火', '水', '木', '金', '土'];

/* ══════════ 本体 ══════════ */
const CrewAssignPage: React.FC = () => {
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [vesselId, setVesselId] = useState('');
  const [ym, setYm] = useState(ymNow());
  const [sheet, setSheet] = useState<SheetData | null>(null);
  const [users, setUsers] = useState<UserLite[]>([]);
  const [loading, setLoading] = useState(false);
  const [showEvent, setShowEvent] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [yearStart, setYearStart] = useState('01-01');
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [exporting, setExporting] = useState(false);
  // 船員が遡って見られる月数（マスタ設定）
  const [pastMonths, setPastMonths] = useState(12);
  // 船員アプリへの公開範囲（''＝未公開。この日までしか船員には見せない）
  const [publishUntil, setPublishUntil] = useState('');
  const [savingPublish, setSavingPublish] = useState(false);
  // 船員が乗下船表を見られる会社だけ「公開範囲」の設定を出す（フラグOFFの会社は今までと同じ画面）
  const crewSheetEnabled = hasFeature('crew_boarding_sheet');
  // 執職日数（当月＋年度累計）。sheet 読込後に取得する
  interface PosDaysRow { user_id: string; user_name: string; employee_code: string; total_on: number; positions: Record<string, number>; duty_days?: Record<string, number> }
  const [posDaysMonth, setPosDaysMonth] = useState<PosDaysRow[]>([]);
  const [posDaysFiscal, setPosDaysFiscal] = useState<PosDaysRow[]>([]);
  const [showDuty, setShowDuty] = useState(false);

  const flash = (msg: string, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), ok ? 3000 : 6000); };

  // 役職マスタ（イベント登録の役職候補・配員基準の選択肢に使用）
  const [positions, setPositions] = useState<string[]>(POSITIONS_FALLBACK);
  useEffect(() => {
    (async () => {
      try {
        const [vs, us, st] = await Promise.all([
          api.get<{ data: Vessel[] }>('/vessels'),
          api.get<{ data: UserLite[] }>('/users'),
          api.get<{ data: { boarding_year_start: string; boarding_sheet_published_until?: string | null; boarding_sheet_past_months?: number } }>('/boarding-sheet/settings'),
        ]);
        const list = vs.data ?? [];
        setVessels(list);
        if (list.length && !vesselId) setVesselId(list[0]!.id);
        setUsers((us.data ?? []).filter(u => u.is_crew_eligible === 1 || ['crew', 'captain'].includes(u.user_role)));
        setYearStart(st.data?.boarding_year_start ?? '01-01');
        setPublishUntil(st.data?.boarding_sheet_published_until ?? '');
        if (typeof st.data?.boarding_sheet_past_months === 'number') setPastMonths(st.data.boarding_sheet_past_months);
      } catch { /* noop */ }
      try {
        const p = await api.get<{ data: Array<{ name: string; is_active: number }> }>('/position-masters');
        const names = (p.data ?? []).filter(x => x.is_active !== 0).map(x => x.name);
        if (names.length) setPositions(names);
      } catch { /* マスタ未整備ならフォールバックを使用 */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 初期表示は「乗組員がいる船」へ自動で切り替える（1回だけ）
  const autoPickedRef = React.useRef(false);
  // Excelメニューから取込のファイル選択を開くためのハンドル
  const importRef = React.useRef<BoardingExcelImportHandle>(null);
  const load = useCallback(async () => {
    if (!vesselId) return;
    setLoading(true);
    try {
      const r = await api.get<{ data: SheetData }>('/boarding-sheet/sheet', { vessel_id: vesselId, month: ym });
      setSheet(r.data);
      if (!autoPickedRef.current) {
        autoPickedRef.current = true;
        const hasCrewHere = r.data.crew.some(c => c.group_vessel_id === vesselId);
        if (!hasCrewHere) {
          // 所属人数が最多の船へ切り替え
          const counts = new Map<string, number>();
          for (const c of r.data.crew) if (c.group_vessel_id) counts.set(c.group_vessel_id, (counts.get(c.group_vessel_id) ?? 0) + 1);
          const best = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
          if (best && best !== vesselId) setVesselId(best);
        }
      }
    } catch (e: any) { flash(e?.message ?? '読み込みに失敗しました', false); }
    setLoading(false);
  }, [vesselId, ym]);
  useEffect(() => { load(); }, [load]);

  // 執職日数の取得（当月＋年度累計）。乗下船表と同じ「乗船日・下船日=乗」ルールで数える
  useEffect(() => {
    if (!sheet) return;
    const monthEnd = `${sheet.month}-${String(sheet.n_days).padStart(2, '0')}`;
    (async () => {
      try {
        const [mo, fi] = await Promise.all([
          api.get<{ data: PosDaysRow[] }>('/boarding-sheet/position-days', { from: `${sheet.month}-01`, to: monthEnd }),
          api.get<{ data: PosDaysRow[] }>('/boarding-sheet/position-days', { from: sheet.fiscal_from, to: monthEnd }),
        ]);
        setPosDaysMonth(mo.data ?? []);
        setPosDaysFiscal(fi.data ?? []);
      } catch { setPosDaysMonth([]); setPosDaysFiscal([]); }
    })();
  }, [sheet]);

  const [y, m] = ym.split('-').map(Number);
  const today = todayYmd();
  const dayMeta = useMemo(() => {
    if (!sheet) return [];
    return Array.from({ length: sheet.n_days }, (_, i) => {
      const dt = new Date(Date.UTC(y!, m! - 1, i + 1));
      const ymd = `${ym}-${String(i + 1).padStart(2, '0')}`;
      return { n: i + 1, wd: dt.getUTCDay(), ymd, isToday: ymd === today };
    });
  }, [sheet, ym, y, m, today]);

  const eventDays = useMemo(() => {
    const s = new Set<string>();
    for (const e of sheet?.events ?? []) s.add(e.event_date);
    return s;
  }, [sheet]);

  // 船ごと表示: 選択船に所属 or この月に1日でもこの船に乗る人（イベント登場者は必ず表に出る）
  const [showOthers, setShowOthers] = useState(false);
  const vesselCrew = useMemo(() => (sheet?.crew ?? []).filter(c =>
    c.group_vessel_id === vesselId || c.days.some(d => d.s === 'on' && !d.o)), [sheet, vesselId]);
  const otherCrew = useMemo(() => (sheet?.crew ?? []).filter(c =>
    !(c.group_vessel_id === vesselId || c.days.some(d => d.s === 'on' && !d.o))), [sheet, vesselId]);

  // 配員不足のある日（バナー用）
  const manningShortages = useMemo(() => {
    if (!sheet) return [];
    const out: string[] = [];
    sheet.daily_manning.forEach((issues, i) => {
      if (issues.length) out.push(`${m}/${i + 1}: ${issues.map(x => `${x.role} ${x.count}/${x.min}人`).join('・')}`);
    });
    return out;
  }, [sheet, m]);

  const exportExcel = async () => {
    if (!vesselId) return;
    setExporting(true);
    try {
      const res = await fetch(`${getApiBase()}/boarding-sheet/export?vessel_id=${vesselId}&month=${ym}`,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error('出力に失敗しました');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const vn = vessels.find(v => v.id === vesselId)?.vessel_name ?? '船';
      a.href = url; a.download = `乗下船表_${vn}_${y}年${m}月.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) { flash(e?.message ?? '出力に失敗しました', false); }
    setExporting(false);
  };

  /**
   * 船員への公開範囲を保存する（月単位）。
   * 船員アプリの乗下船表は「公開した月まで」を丸ごと表示する（未設定なら何も表示されない）。
   * 月の途中で切ると船員が月末の予定を確認できないため、月単位で切り替える。
   */
  const savePublish = async (month: string | null) => {
    setSavingPublish(true);
    try {
      await api.put('/boarding-sheet/publish', { published_month: month });
      // サーバーは月末日に丸めて保存する
      setPublishUntil(month ? `${month}-${String(new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).getUTCDate()).padStart(2, '0')}` : '');
      flash(month ? `船員には ${month.replace('-', '年')}月分までを公開しました` : '船員への公開を停止しました');
    } catch (e: any) { flash(e?.message ?? '保存に失敗しました', false); }
    setSavingPublish(false);
  };
  /** 船員が遡れる月数を保存する（マスタ設定） */
  const savePastMonths = async (n: number) => {
    setSavingPublish(true);
    try {
      await api.put('/boarding-sheet/settings', { boarding_sheet_past_months: n });
      setPastMonths(n);
      flash(n === 0 ? '船員には当月だけを見せます' : `船員は過去 ${n} ヶ月分まで見られます`);
    } catch (e: any) { flash(e?.message ?? '保存に失敗しました', false); }
    setSavingPublish(false);
  };

  /** 公開されている月（YYYY-MM）。保存値は月末日なので月だけ取り出す */
  const publishedMonth = publishUntil ? publishUntil.slice(0, 7) : '';

  const removeEvent = async (e: ChangeEvent) => {
    const who = e.items.map(i => i.user_name).filter(Boolean).join('、');
    if (!window.confirm(`${e.event_date} ${e.port_name ?? ''} のイベントを削除しますか？\n（${who || '明細なし'} の発令予約・反映済みの乗下船も巻き戻されます）`)) return;
    try {
      await api.del(`/boarding-sheet/events/${e.id}`);
      flash('イベントを削除し、乗下船を巻き戻しました');
      load();
    } catch (er: any) { flash(er?.message ?? '削除に失敗しました', false); }
  };

  /* ── セル描画 ── */
  const cellStyle = (c: DayCell, isToday: boolean): React.CSSProperties => {
    const base: React.CSSProperties = {
      minWidth: 26, maxWidth: 26, height: 30, padding: 0, textAlign: 'center',
      fontSize: 10, fontWeight: 700, position: 'relative',
      borderRight: '1px solid #EDF1F5', borderBottom: '1px solid #EDF1F5',
      background: isToday ? '#FFFBEB' : 'white',
    };
    if (c.s === 'on') {
      if (c.o) {
        // 他船に乗船（応援）
        base.background = c.b || c.d ? '#7C3AED' : '#E7E0FA';
        base.color = c.b || c.d ? 'white' : '#6D28D9';
      } else {
        base.background = c.b || c.d ? '#0E5AA7' : '#BFE0F5';
        base.color = c.b || c.d ? 'white' : '#0B4E8F';
      }
    } else if (c.s === 'off') {
      base.background = isToday ? '#FBF6E4' : '#F4F6F8';
      base.color = '#A6B0BC';
    } else {
      base.color = '#D5DBE2';
    }
    // 希望休はセル全体で表現する（点は廃止）
    if (c.w === 'approved') {
      if (c.s === 'on') {
        // 乗船と重複＝データ不整合。隠さず赤で可視化する
        base.background = c.b || c.d ? '#DC2626' : '#FECACA';
        base.color = c.b || c.d ? 'white' : '#B91C1C';
      } else {
        base.background = '#BBF7D0';
        base.color = '#166534';
      }
    } else if (c.w === 'pending') {
      base.backgroundImage =
        'repeating-linear-gradient(135deg, rgba(245,158,11,0.38) 0px, rgba(245,158,11,0.38) 3px, transparent 3px, transparent 7px)';
    }
    // 執職（役職と異なる職務に従事）＝金色の下線でハイライト
    if (c.dp && c.s === 'on') {
      base.boxShadow = 'inset 0 -4px 0 #F59E0B';
    }
    return base;
  };
  const cellText = (c: DayCell) =>
    (c.s === 'on' ? (c.b ? '▲' : c.d ? '▼' : (c.w === 'approved' ? '⚠' : '')) : c.s === 'off' ? '休' : '');
  const cellTitle = (c: DayCell, u: CrewRow, dm: { ymd: string }) => {
    const parts: string[] = [`${u.user_name} ${dm.ymd}`];
    if (c.s === 'on') parts.push(`${c.b ? '乗船日' : c.d ? '下船日' : '乗船中'}${c.o ? `（${c.o}に応援乗船）` : ''}`);
    else if (c.s === 'off') parts.push('休（陸上）');
    if (c.dp) parts.push(`執職: ${c.dp}として職務に従事`);
    if (c.w === 'approved') parts.push(c.s === 'on' ? '希望休（承認済）⚠ 乗船と重複しています' : '希望休（承認済）');
    else if (c.w === 'pending') parts.push('希望休（申請中）');
    return parts.join(' / ');
  };

  /** 執職バッジ（執職ごとに系統色で塗り分け。月内で執職が変わった人は「→」で並べる） */
  const roleBadge = (positions: string[]) => {
    if (!positions.length) return <span style={{ color: '#C2CBD6', fontSize: 11 }}>—</span>;
    const title = `執職: ${positions.join(' → ')}`;
    return (
      <span title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, whiteSpace: 'nowrap', maxWidth: 118, overflow: 'hidden' }}>
        {positions.map((p, i) => {
          const c = positionColor(p);
          return (
            <React.Fragment key={i}>
              {i > 0 && <span style={{ fontSize: 9, color: '#A6B0BC', fontWeight: 800 }}>→</span>}
              <span style={{ fontSize: 11, fontWeight: 800, color: c.fg, background: c.bg, borderRadius: 6, padding: '3px 6px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {p}
              </span>
            </React.Fragment>
          );
        })}
      </span>
    );
  };

  /** 取込フォーマットのダウンロード（従来の乗下船表と同じレイアウト・当月から3か月分） */
  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${getApiBase()}/boarding-sheet/template?vessel_id=${vesselId}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error('ダウンロードに失敗しました');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const vn = vessels.find(v => v.id === vesselId)?.vessel_name ?? '船';
      a.href = url; a.download = `乗下船表_取込フォーマット_${vn}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) { flash(e?.message ?? 'ダウンロードに失敗しました', false); }
  };

  const th: React.CSSProperties = { padding: '6px 8px', fontSize: 11, fontWeight: 700, color: '#5A6B7E', background: '#F7F9FB', borderBottom: '2px solid #E3E9EF', whiteSpace: 'nowrap' };

  /** 乗組員1行の描画（所属船セクション・他船セクション共通） */
  const renderCrewRow = (u: CrewRow, dimmed = false) => {
    // この月に乗っている「他の船」（横断ナビ用チップ。クリックでその船の表へ）
    const crossVessels = new Map<string, string>();
    for (const d of u.days) if (d.oi && d.o) crossVessels.set(d.oi, d.o);
    return (
    <tr key={u.user_id} style={dimmed ? { opacity: 0.75 } : undefined}>
      <td style={{ position: 'sticky', left: 0, zIndex: 2, background: 'white', padding: '4px 8px', fontSize: 12.5, fontWeight: 700, color: '#16283B', borderBottom: '1px solid #EDF1F5', whiteSpace: 'nowrap' }}>
        {u.user_name}
        {[...crossVessels.entries()].map(([vid, vname]) => (
          <button key={vid} onClick={() => setVesselId(vid)}
            title={`この月は「${vname}」にも乗船。クリックで${vname}の乗下船表へ`}
            style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 700, color: '#7C3AED', background: '#F3EFFD', borderRadius: 5, padding: '1px 6px', border: '1px solid #DCCDF6', cursor: 'pointer' }}>
            ⇄{vname}
          </button>
        ))}
        {dimmed && crossVessels.size === 0 && u.group_vessel_name && (
          <span style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 700, color: '#7C3AED', background: '#F3EFFD', borderRadius: 5, padding: '1px 5px' }}>{u.group_vessel_name}</span>
        )}
        {dimmed && !u.group_vessel_name && (
          <span style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 700, color: '#8395A7', background: '#F1F5F9', borderRadius: 5, padding: '1px 5px' }}>未配属</span>
        )}
      </td>
      <td style={{ position: 'sticky', left: 118, zIndex: 2, background: 'white', padding: '4px 6px', borderBottom: '1px solid #EDF1F5', borderRight: '2px solid #E3E9EF', whiteSpace: 'nowrap' }}>
        {/* 辞令上の役職（ユーザー管理）。未設定の会社は従来どおり配乗役職を出す */}
        {roleBadge(u.rank_position ? [u.rank_position] : u.positions)}
        {/* 執職＝この月に辞令と異なる職務に従事した分（執職期間の登録 or 乗船中の役職） */}
        {[...new Set([
          ...u.duties.map(dp => dp.duty_position),
          ...(u.rank_position ? u.positions : []),
        ])].filter(p => u.rank_position ? p !== u.rank_position : !u.positions.includes(p)).map(dp => (
          <span key={dp} title={`この月に「${dp}」として職務に従事した期間があります（執職）`}
            style={{ display: 'inline-block', marginLeft: 3, fontSize: 10, fontWeight: 800, color: '#92600A', background: '#FDF3E3', border: '1px solid #F2DCA6', borderRadius: 6, padding: '2px 5px' }}>
            執 {dp}
          </span>
        ))}
      </td>
      {u.days.map((c, i) => (
        <td key={i}
          style={{ ...cellStyle(c, dayMeta[i]?.isToday ?? false), cursor: c.oi ? 'pointer' : c.w ? 'help' : undefined }}
          title={cellTitle(c, u, dayMeta[i]!) + (c.oi ? '（クリックでその船の表へ）' : '')}
          onClick={c.oi ? () => setVesselId(c.oi!) : undefined}>
          {cellText(c)}
        </td>
      ))}
      <td style={{ textAlign: 'center', fontSize: 13, fontWeight: 800, color: '#0E5AA7', background: '#F5FAFE', borderLeft: '2px solid #E3E9EF', borderBottom: '1px solid #EDF1F5' }}>{u.month_on}</td>
      <td style={{ textAlign: 'center', fontSize: 12.5, color: '#5A6B7E', borderBottom: '1px solid #EDF1F5' }}>{u.month_off}</td>
      <td style={{ textAlign: 'center', fontSize: 13, fontWeight: 800, color: '#166534', background: '#F4FAF5', borderBottom: '1px solid #EDF1F5' }}>{u.cum_on}</td>
      <td style={{ textAlign: 'center', fontSize: 12.5, color: '#5A6B7E', borderBottom: '1px solid #EDF1F5' }}>{u.cum_off}</td>
    </tr>
    );
  };

  return (
    <div style={{ padding: '20px 22px', maxWidth: 1600, margin: '0 auto' }}>
      {/* ヘッダー */}
      {/* この画面の役割と反映タイミング（配乗シミュレーション／乗下船表／乗下船記録の使い分け） */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#1e3a8a' }}>
        <span><strong>この画面:</strong> 配乗・発令の結果を月表で見る画面です。執職期間（乗船中に執る職）の登録はここ、配乗そのものの変更は配乗シミュレーションで行います。</span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 12 }}><Link to="/crew-sim" style={{ fontWeight: 800, color: '#1d4ed8', textDecoration: 'none' }}>配乗シミュレーション →</Link><Link to="/boarding" style={{ fontWeight: 800, color: '#1d4ed8', textDecoration: 'none' }}>乗下船記録 →</Link></span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'linear-gradient(135deg,#0E5AA7,#2EA8D5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CalendarDays size={20} color="white" />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#16283B' }}>乗下船表</h1>
            <div style={{ fontSize: 11.5, color: '#8395A7' }}>配乗・発令と自動連動 ／ 累計は {sheet ? `${sheet.fiscal_from} 起算（毎年${Number(yearStart.slice(0, 2))}/${Number(yearStart.slice(3))}リセット）` : '—'}</div>
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {vessels.length > 1 && (
          <select value={vesselId} onChange={e => setVesselId(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 10, border: '1.5px solid #DCE3EA', fontSize: 13, fontWeight: 600, color: '#2B3B4E', background: 'white' }}>
            {vessels.map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}
          </select>
        )}
        {vessels.length === 1 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, background: '#EAF3FB', color: '#0E5AA7', fontSize: 13, fontWeight: 700 }}>
            <Ship size={15} />{vessels[0]?.vessel_name}
          </span>
        )}
        <div style={{ display: 'flex', alignItems: 'center', border: '1.5px solid #DCE3EA', borderRadius: 10, overflow: 'hidden', background: 'white' }}>
          <button onClick={() => setYm(ymAdd(ym, -1))} style={{ border: 'none', background: 'none', padding: '8px 10px', cursor: 'pointer', color: '#5A6B7E' }}><ChevronLeft size={16} /></button>
          <span style={{ fontSize: 14, fontWeight: 800, color: '#16283B', minWidth: 96, textAlign: 'center' }}>{y}年{m}月</span>
          <button onClick={() => setYm(ymAdd(ym, 1))} style={{ border: 'none', background: 'none', padding: '8px 10px', cursor: 'pointer', color: '#5A6B7E' }}><ChevronRight size={16} /></button>
        </div>
        <button onClick={() => setYm(ymNow())} style={{ padding: '8px 12px', borderRadius: 10, border: '1.5px solid #DCE3EA', background: 'white', fontSize: 12.5, fontWeight: 600, color: '#5A6B7E', cursor: 'pointer' }}>今月</button>
        <button onClick={() => setShowSettings(true)} title="累計の起算日を設定"
          style={{ padding: '8px 10px', borderRadius: 10, border: '1.5px solid #DCE3EA', background: 'white', color: '#5A6B7E', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          <Settings2 size={16} />
        </button>
        <ExcelMenu items={[
          { icon: <Download size={15} />, label: exporting ? '出力中…' : 'この月をExcelで出力', desc: '表示中の乗下船表を従来様式でダウンロード', onClick: exportExcel },
          { icon: <FileUp size={15} />, label: 'Excelから取り込む', desc: 'イベント表を読み込んで乗下船・発令予約を一括登録', onClick: () => importRef.current?.openPicker() },
          { icon: <FileDown size={15} />, label: '取込用フォーマットを入手', desc: '記入例・乗組員一覧つきの空Excelをダウンロード', onClick: downloadTemplate },
        ]} />
        <BoardingExcelImport
          ref={importRef} hideTrigger
          vessels={vessels}
          users={users.map(u => ({ id: u.id, user_name: u.user_name }))}
          onImported={load}
        />

        <button onClick={() => setShowEvent(true)}
          style={{ padding: '8px 16px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#0E5AA7,#2EA8D5)', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 8px rgba(14,90,167,0.3)' }}>
          <Plus size={15} />乗下船イベント
        </button>
      </div>

      {/* 凡例 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10, fontSize: 11.5, color: '#5A6B7E', flexWrap: 'wrap' }}>
        <span><span style={{ display: 'inline-block', width: 14, height: 11, background: '#BFE0F5', borderRadius: 3, verticalAlign: -1, marginRight: 4 }} />乗船中</span>
        <span><span style={{ display: 'inline-block', width: 14, height: 11, background: '#0E5AA7', borderRadius: 3, verticalAlign: -1, marginRight: 4, color: 'white', fontSize: 8, textAlign: 'center', lineHeight: '11px', fontWeight: 700 }}>▲</span>乗船日</span>
        <span><span style={{ display: 'inline-block', width: 14, height: 11, background: '#0E5AA7', borderRadius: 3, verticalAlign: -1, marginRight: 4, color: 'white', fontSize: 8, textAlign: 'center', lineHeight: '11px', fontWeight: 700 }}>▼</span>下船日</span>
        <span style={{ color: '#A6B0BC' }}>休＝陸上休み</span>
        <span><span style={{ display: 'inline-block', width: 14, height: 11, background: '#E7E0FA', borderRadius: 3, verticalAlign: -1, marginRight: 4 }} />他船へ応援（クリックでその船へ）</span>
        <span><span style={{ display: 'inline-block', width: 14, height: 11, borderRadius: 3, verticalAlign: -1, marginRight: 4, border: '1px solid #E3C98A', backgroundImage: 'repeating-linear-gradient(135deg, rgba(245,158,11,0.45) 0px, rgba(245,158,11,0.45) 3px, transparent 3px, transparent 7px)' }} />希望休（申請中）</span>
        <span><span style={{ display: 'inline-block', width: 14, height: 11, background: '#BBF7D0', borderRadius: 3, verticalAlign: -1, marginRight: 4, color: '#166534', fontSize: 8, textAlign: 'center', lineHeight: '11px', fontWeight: 700 }}>休</span>希望休（承認済）</span>
        <span><span style={{ display: 'inline-block', width: 14, height: 11, background: '#FECACA', borderRadius: 3, verticalAlign: -1, marginRight: 4, color: '#B91C1C', fontSize: 8, textAlign: 'center', lineHeight: '11px', fontWeight: 700 }}>⚠</span>乗船と希望休が重複（要修正）</span>
        <span><span style={{ display: 'inline-block', width: 14, height: 11, background: '#BFE0F5', borderRadius: 3, verticalAlign: -1, marginRight: 4, boxShadow: 'inset 0 -3px 0 #F59E0B' }} />執職（役職と異なる職務に従事）</span>
        <span><span style={{ display: 'inline-block', width: 14, height: 11, background: '#FDF3E3', borderRadius: 3, verticalAlign: -1, marginRight: 4, fontSize: 8, textAlign: 'center', lineHeight: '11px' }}>🔧</span>ドック・入渠（配員チェック対象外）</span>
        <span><span style={{ display: 'inline-block', width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: '8px solid #0E5AA7', verticalAlign: 0, marginRight: 4 }} />イベントあり（下部に詳細）</span>
        <span style={{ marginLeft: 'auto', color: '#8395A7' }}>※乗船日・下船日はどちらも「乗」1日でカウントします</span>
        <button onClick={load} title="再読込" style={{ border: 'none', background: 'none', color: '#8395A7', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><RefreshCw size={13} /></button>
      </div>

      {/* 配員不足バナー */}
      {manningShortages.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#FEF3F2', border: '1px solid #F4C7C3', borderRadius: 12, padding: '10px 14px', marginBottom: 10 }}>
          <AlertTriangle size={16} color="#B42318" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 12.5, color: '#912018', fontWeight: 600 }}>
            配員基準を満たさない日があります：{manningShortages.slice(0, 4).join('　')}
            {manningShortages.length > 4 && `　ほか${manningShortages.length - 4}日`}
          </div>
        </div>
      )}

      {/* 船員への公開範囲（機能フラグ crew_boarding_sheet がONの会社だけ） */}
      {crewSheetEnabled && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: publishUntil ? '#F5FAFE' : '#FEF6E7', border: `1px solid ${publishUntil ? '#CFE4F5' : '#F2DCA6'}`, borderRadius: 12, padding: '10px 14px', marginBottom: 10 }}>
          {publishUntil
            ? <Eye size={16} color="#0E5AA7" style={{ flexShrink: 0 }} />
            : <EyeOff size={16} color="#92600A" style={{ flexShrink: 0 }} />}
          <span style={{ fontSize: 13, fontWeight: 800, color: publishUntil ? '#0E5AA7' : '#92600A' }}>船員への公開範囲</span>
          <input type="month" value={publishedMonth} onChange={e => savePublish(e.target.value || null)}
            disabled={savingPublish}
            style={{ padding: '7px 10px', borderRadius: 9, border: '1.5px solid #DCE3EA', fontSize: 13, fontWeight: 600, color: '#2B3B4E', background: 'white' }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#2B3B4E' }}>月分まで公開</span>
          <button onClick={() => savePublish(ym)} disabled={savingPublish}
            title={`${y}年${m}月分までを船員に公開します`}
            style={{ padding: '7px 14px', borderRadius: 9, border: '1.5px solid #DCE3EA', background: 'white', fontSize: 12.5, fontWeight: 700, color: '#5A6B7E', cursor: savingPublish ? 'not-allowed' : 'pointer' }}>
            {savingPublish ? '保存中…' : `表示中の${m}月分まで公開`}
          </button>
          {publishUntil && (
            <button onClick={() => savePublish(null)} disabled={savingPublish}
              style={{ padding: '7px 14px', borderRadius: 9, border: '1.5px solid #F4C7C3', background: 'white', fontSize: 12.5, fontWeight: 700, color: '#B42318', cursor: 'pointer' }}>
              公開を停止
            </button>
          )}
          <span style={{ width: 1, height: 22, background: '#DCE3EA' }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#2B3B4E' }}>過去</span>
          <select value={pastMonths} onChange={e => savePastMonths(Number(e.target.value))} disabled={savingPublish}
            style={{ padding: '7px 10px', borderRadius: 9, border: '1.5px solid #DCE3EA', fontSize: 13, fontWeight: 600, color: '#2B3B4E', background: 'white' }}>
            {[0, 1, 3, 6, 12, 24, 36, 60].map(n => (
              <option key={n} value={n}>{n === 0 ? '当月のみ' : `${n}ヶ月`}</option>
            ))}
          </select>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#2B3B4E' }}>分まで遡れる</span>
          <span style={{ fontSize: 11.5, color: '#5A6B7E', marginLeft: 'auto' }}>
            {publishedMonth
              ? `船員アプリでは ${publishedMonth.replace('-', '年')}月分までの乗/休が見えます（累計日数は表示されません）`
              : '未設定のため、船員アプリには「まだ公開されていません」と表示されます'}
          </span>
        </div>
      )}

      {/* グリッド */}
      <div style={{ background: 'white', borderRadius: 14, boxShadow: '0 1px 4px rgba(22,40,59,0.08)', overflow: 'hidden', marginBottom: 18 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: 'max-content', minWidth: '100%' }}>
            <thead>
              <tr>
                <th style={{ ...th, position: 'sticky', left: 0, zIndex: 3, minWidth: 118, textAlign: 'left', background: '#F7F9FB' }}>名前</th>
                <th style={{ ...th, position: 'sticky', left: 118, zIndex: 3, minWidth: 86, textAlign: 'left', background: '#F7F9FB', borderRight: '2px solid #E3E9EF' }}>役職</th>
                {dayMeta.map((dm, i) => {
                  const dock = sheet?.dock_days?.[i];
                  return (
                  <th key={dm.n} title={dock ? 'ドック・入渠中（配員チェック対象外）' : undefined}
                    style={{ ...th, padding: '4px 0', minWidth: 26, maxWidth: 26, textAlign: 'center', background: dock ? '#FDF3E3' : dm.isToday ? '#FFF3C9' : '#F7F9FB' }}>
                    <div style={{ color: dm.wd === 0 ? '#DC2626' : dm.wd === 6 ? '#2563EB' : '#5A6B7E', fontSize: 10.5 }}>{dm.n}</div>
                    <div style={{ color: dm.wd === 0 ? '#DC2626' : dm.wd === 6 ? '#2563EB' : '#A6B0BC', fontSize: 9 }}>{YOUBI[dm.wd]}</div>
                    {dock && <div style={{ fontSize: 8, lineHeight: '8px' }}>🔧</div>}
                    {eventDays.has(dm.ymd) && <div style={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderBottom: '6px solid #0E5AA7', margin: '1px auto 0' }} />}
                  </th>
                  );
                })}
                <th style={{ ...th, minWidth: 44, textAlign: 'center', borderLeft: '2px solid #E3E9EF', background: '#EAF3FB', color: '#0E5AA7' }}>月間<br />乗</th>
                <th style={{ ...th, minWidth: 44, textAlign: 'center', background: '#F7F9FB' }}>月間<br />休</th>
                <th style={{ ...th, minWidth: 48, textAlign: 'center', background: '#E4F0E7', color: '#166534' }} title={sheet ? `${sheet.fiscal_from} から当月末まで` : ''}>累計<br />乗</th>
                <th style={{ ...th, minWidth: 48, textAlign: 'center', background: '#F7F9FB' }}>累計<br />休</th>
              </tr>
            </thead>
            <tbody>
              {vesselCrew.map(u => renderCrewRow(u))}
              {vesselCrew.length === 0 && sheet && (
                <tr><td colSpan={2 + sheet.n_days + 4} style={{ padding: 14, fontSize: 12.5, color: '#8395A7', textAlign: 'center' }}>この月にこの船へ配乗された乗組員はいません</td></tr>
              )}
              {/* 日別合計 */}
              {sheet && (
                <>
                  <tr>
                    <td style={{ position: 'sticky', left: 0, zIndex: 2, background: '#F7F9FB', padding: '4px 8px', fontSize: 11, fontWeight: 700, color: '#0E5AA7', borderTop: '2px solid #E3E9EF' }} >乗船人数</td>
                    <td style={{ position: 'sticky', left: 118, zIndex: 2, background: '#F7F9FB', borderTop: '2px solid #E3E9EF', borderRight: '2px solid #E3E9EF' }} />
                    {sheet.daily_on.map((v, i) => (
                      <td key={i} style={{ textAlign: 'center', fontSize: 10.5, fontWeight: 700, color: '#0E5AA7', background: '#F7F9FB', borderTop: '2px solid #E3E9EF' }}>{v}</td>
                    ))}
                    <td colSpan={4} style={{ background: '#F7F9FB', borderTop: '2px solid #E3E9EF' }} />
                  </tr>
                  <tr>
                    <td style={{ position: 'sticky', left: 0, zIndex: 2, background: '#F7F9FB', padding: '4px 8px', fontSize: 11, fontWeight: 700, color: '#A6B0BC' }}>休人数</td>
                    <td style={{ position: 'sticky', left: 118, zIndex: 2, background: '#F7F9FB', borderRight: '2px solid #E3E9EF' }} />
                    {sheet.daily_off.map((v, i) => (
                      <td key={i} style={{ textAlign: 'center', fontSize: 10.5, color: '#A6B0BC', background: '#F7F9FB' }}>{v}</td>
                    ))}
                    <td colSpan={4} style={{ background: '#F7F9FB' }} />
                  </tr>
                  {/* 配員チェック行（基準がある場合のみ） */}
                  {sheet.manning_rules.length > 0 && (
                    <tr>
                      <td style={{ position: 'sticky', left: 0, zIndex: 2, background: '#F7F9FB', padding: '4px 8px', fontSize: 11, fontWeight: 700, color: '#B42318' }}>配員チェック</td>
                      <td style={{ position: 'sticky', left: 118, zIndex: 2, background: '#F7F9FB', borderRight: '2px solid #E3E9EF', fontSize: 9.5, color: '#8395A7', padding: '0 6px', whiteSpace: 'nowrap' }}>
                        {sheet.manning_rules.map(r => `${r.role_name}${r.min_count}`).join('・')}
                      </td>
                      {sheet.daily_manning.map((issues, i) => {
                        const dock = sheet.dock_days?.[i];
                        return (
                        <td key={i} title={dock ? 'ドック・入渠中（配員チェック対象外）' : issues.length ? issues.map(x => `${x.role} ${x.count}/${x.min}人`).join('、') : '基準を満たしています'}
                          style={{ textAlign: 'center', fontSize: dock ? 9 : 10.5, fontWeight: 800, background: dock ? '#FDF3E3' : issues.length ? '#FCE9E7' : '#F7F9FB', color: dock ? '#B45309' : issues.length ? '#B42318' : '#B5C6B8' }}>
                          {dock ? '🔧' : issues.length ? '⚠' : '✓'}
                        </td>
                        );
                      })}
                      <td colSpan={4} style={{ background: '#F7F9FB' }} />
                    </tr>
                  )}
                  {/* 他船・未配属の乗組員（折りたたみ） */}
                  {otherCrew.length > 0 && (
                    <tr>
                      <td colSpan={2 + sheet.n_days + 4} style={{ padding: 0, borderTop: '2px solid #E3E9EF' }}>
                        <button onClick={() => setShowOthers(v => !v)}
                          style={{ width: '100%', border: 'none', background: '#FAFBFC', padding: '8px 12px', fontSize: 12, fontWeight: 700, color: '#5A6B7E', cursor: 'pointer', textAlign: 'left' }}>
                          {showOthers ? '▼' : '▶'} 他船・未配属の乗組員（{otherCrew.length}名）{showOthers ? 'を隠す' : 'を表示'}
                        </button>
                      </td>
                    </tr>
                  )}
                  {showOthers && otherCrew.map(u => renderCrewRow(u, true))}
                </>
              )}
            </tbody>
          </table>
        </div>
        {loading && <div style={{ padding: 18, textAlign: 'center', fontSize: 13, color: '#8395A7' }}>読み込み中…</div>}
        {!loading && sheet && sheet.crew.length === 0 && <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: '#8395A7' }}>乗組員が登録されていません</div>}
      </div>

      {/* 乗下船イベント */}
      <div style={{ background: 'white', borderRadius: 14, boxShadow: '0 1px 4px rgba(22,40,59,0.08)', padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <Anchor size={16} color="#0E5AA7" />
          <span style={{ fontSize: 14.5, fontWeight: 800, color: '#16283B' }}>乗下船イベント（{m}月）</span>
          <span style={{ fontSize: 11.5, color: '#8395A7' }}>— 従来のメモ書きを構造化して管理します。登録すると発令予約が自動作成されます</span>
        </div>
        {(sheet?.events ?? []).length === 0 && (
          <div style={{ padding: '14px 0', fontSize: 12.5, color: '#A6B0BC' }}>この月のイベントはまだありません。「＋乗下船イベント」から登録できます。</div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(sheet?.events ?? []).map(e => {
            const t = EVENT_TYPE[e.event_type as keyof typeof EVENT_TYPE] ?? EVENT_TYPE.other;
            return (
              <div key={e.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 12px', borderRadius: 10, border: '1px solid #E8EDF2', background: '#FCFDFE' }}>
                <div style={{ minWidth: 84, fontSize: 13, fontWeight: 800, color: '#16283B' }}>
                  {Number(e.event_date.slice(5, 7))}/{Number(e.event_date.slice(8, 10))}
                  {e.end_date && <span style={{ color: '#8395A7', fontWeight: 600 }}>〜{Number(e.end_date.slice(5, 7))}/{Number(e.end_date.slice(8, 10))}</span>}
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: t.color, background: t.bg, borderRadius: 6, padding: '3px 8px', whiteSpace: 'nowrap' }}>{t.label}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {e.port_name && <span style={{ fontSize: 13, fontWeight: 700, color: '#2B3B4E' }}>⚓ {e.port_name}</span>}
                    {e.berth_note && <span style={{ fontSize: 11.5, color: '#8395A7' }}>{e.berth_note}</span>}
                    {e.items.filter(i => i.action === 'board').map((i, k) => (
                      <span key={`b${k}`} style={{ fontSize: 11.5, fontWeight: 700, color: '#166534', background: '#E4F0E7', borderRadius: 6, padding: '3px 8px' }}>
                        ▲乗船 {i.user_name}{i.position ? `（${i.position}）` : ''}
                      </span>
                    ))}
                    {e.items.filter(i => i.action === 'disembark').map((i, k) => (
                      <span key={`d${k}`} style={{ fontSize: 11.5, fontWeight: 700, color: '#B42318', background: '#FCE9E7', borderRadius: 6, padding: '3px 8px' }}>
                        ▼下船 {i.user_name}{i.position ? `（${i.position}）` : ''}
                      </span>
                    ))}
                  </div>
                  {(e.note || e.items.some(i => i.note)) && (
                    <div style={{ marginTop: 4, fontSize: 11.5, color: '#5A6B7E' }}>
                      {e.note && <span>※{e.note}</span>}
                      {e.items.filter(i => i.note).map((i, k) => <span key={k} style={{ marginLeft: 8 }}>※{i.user_name}: {i.note}</span>)}
                    </div>
                  )}
                </div>
                <button onClick={() => removeEvent(e)} title="イベントを削除（発令も巻き戻し）"
                  style={{ border: 'none', background: 'none', color: '#C2CBD6', cursor: 'pointer', padding: 4 }}
                  onMouseEnter={ev => (ev.currentTarget.style.color = '#DC2626')}
                  onMouseLeave={ev => (ev.currentTarget.style.color = '#C2CBD6')}>
                  <Trash2 size={15} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* 執職日数（当月＋年度累計） */}
      {sheet && vesselCrew.length > 0 && (
        <div style={{ background: 'white', borderRadius: 14, boxShadow: '0 1px 4px rgba(22,40,59,0.08)', padding: '16px 18px', marginTop: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <Ship size={16} color="#0E5AA7" />
            <span style={{ fontSize: 14.5, fontWeight: 800, color: '#16283B' }}>執職日数（当月）</span>
            <span style={{ fontSize: 11.5, color: '#8395A7' }}>
              — 職務上執った役職ごとの当月の乗船日数（括弧内は {sheet.fiscal_from} 起算の年度累計）。海技資格の受験資格確認・手当計算の基礎資料になります
            </span>
            <button onClick={() => setShowDuty(true)}
              style={{ marginLeft: 'auto', padding: '6px 12px', borderRadius: 9, border: '1.5px solid #F2DCA6', background: '#FDF3E3', fontSize: 12, fontWeight: 700, color: '#92600A', cursor: 'pointer' }}>
              ＋ 執職期間を登録
            </button>
          </div>
          <div style={{ fontSize: 11, color: '#8395A7', marginBottom: 10 }}>
            執職＝辞令上の役職とは別に、実際に従事した職務。例）役職は二等航海士だが、一等航海士の休暇中はその職務を執る——その期間を登録すると
            <span style={{ color: '#92600A', fontWeight: 700 }}>金色の下線</span>でハイライトされ、日数も執職で集計されます。
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {vesselCrew.map(u => {
              const fi = posDaysFiscal.find(x => x.user_id === u.user_id);
              const mo = posDaysMonth.find(x => x.user_id === u.user_id);
              if (!fi && !mo) return null;
              const posNames = [...new Set([...Object.keys(fi?.positions ?? {}), ...Object.keys(mo?.positions ?? {})])];
              return (
                <div key={u.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '7px 10px', borderRadius: 10, border: '1px solid #E8EDF2', background: '#FCFDFE' }}>
                  <span style={{ minWidth: 110, fontSize: 12.5, fontWeight: 700, color: '#16283B' }}>{u.user_name}</span>
                  {posNames.map(p => {
                    const c = positionColor(p);
                    const moN = mo?.positions[p] ?? 0;
                    const dutyMoN = mo?.duty_days?.[p] ?? 0;
                    const dutyFiN = fi?.duty_days?.[p] ?? 0;
                    if (!moN && !dutyMoN) return null;   // 当月に従事していない執職は出さない
                    return (
                      <span key={p}
                        title={`${p}: 当月 ${moN}日${dutyMoN ? `（うち役職と異なる執職として ${dutyMoN}日）` : ''} ／ 年度累計 ${fi?.positions[p] ?? 0}日${dutyFiN ? `（執職 ${dutyFiN}日）` : ''}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 700, color: c.fg, background: c.bg, borderRadius: 8, padding: '4px 10px',
                                 boxShadow: dutyMoN ? 'inset 0 0 0 1.5px #F59E0B' : undefined }}>
                        {dutyMoN > 0 && <span style={{ fontSize: 9.5, fontWeight: 800, color: '#92600A', background: '#FDF3E3', borderRadius: 4, padding: '1px 4px' }}>執</span>}
                        {p}
                        <span style={{ fontWeight: 800 }}>{moN}日</span>
                        <span style={{ fontSize: 10, opacity: 0.7 }}>（年度計{fi?.positions[p] ?? 0}日）</span>
                      </span>
                    );
                  })}
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#8395A7' }}>当月計 {mo?.total_on ?? 0}日 ／ 年度計 {fi?.total_on ?? 0}日</span>
                </div>
              );
            })}
          </div>
          {/* この月の執職期間（削除可能） */}
          {vesselCrew.some(u => u.duties.length > 0) && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 11.5, fontWeight: 800, color: '#92600A' }}>この月の執職期間</span>
              {vesselCrew.flatMap(u => u.duties.map(dp => (
                <div key={dp.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#5A6B7E', padding: '4px 8px', borderRadius: 8, background: '#FDF9F0', border: '1px solid #F2E7CC' }}>
                  <span style={{ fontWeight: 700, color: '#16283B' }}>{u.user_name}</span>
                  <span style={{ fontWeight: 800, color: '#92600A' }}>{dp.duty_position} として従事</span>
                  <span>{dp.start_date} 〜 {dp.end_date ?? '継続中'}</span>
                  {dp.note && <span style={{ color: '#8395A7' }}>※{dp.note}</span>}
                  <button onClick={async () => {
                    if (!window.confirm(`${u.user_name} の執職期間（${dp.duty_position}）を削除しますか？`)) return;
                    try { await api.del(`/boarding-sheet/duty-periods/${dp.id}`); flash('執職期間を削除しました'); load(); }
                    catch (e: any) { flash(e?.message ?? '削除に失敗しました', false); }
                  }} style={{ marginLeft: 'auto', border: 'none', background: 'none', color: '#C2CBD6', cursor: 'pointer', padding: 2 }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              )))}
            </div>
          )}
          <div style={{ marginTop: 8, fontSize: 10.5, color: '#A6B0BC' }}>
            ※Excel出力（この月をExcelで出力）にも「執職日数」シートが含まれます。全乗組員ぶんはレポートの「執職日数集計」から出力できます。
          </div>
        </div>
      )}

      {showDuty && sheet && (
        <DutyModal
          ym={ym} users={users} positions={positions} vesselId={vesselId}
          onClose={() => setShowDuty(false)}
          onSaved={() => { setShowDuty(false); flash('執職期間を登録しました'); load(); }}
          onError={(msg) => flash(msg, false)}
        />
      )}

      {showEvent && sheet && (
        <EventModal
          ym={ym} vesselId={vesselId} users={users} rests={sheet.rests} positions={positions}
          onClose={() => setShowEvent(false)}
          onSaved={(msg) => { setShowEvent(false); flash(msg); load(); }}
          onError={(msg) => flash(msg, false)}
        />
      )}
      {showSettings && (
        <SettingsModal
          yearStart={yearStart} vesselId={vesselId} positions={positions}
          vesselName={vessels.find(v => v.id === vesselId)?.vessel_name ?? ''}
          onClose={() => setShowSettings(false)}
          onSaved={(v) => { setYearStart(v); setShowSettings(false); flash('設定を保存しました'); load(); }}
          onError={(msg) => flash(msg, false)}
        />
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: toast.ok ? '#16283B' : '#B42318', color: 'white', padding: '11px 22px', borderRadius: 12, fontSize: 13.5, fontWeight: 600, zIndex: 300, boxShadow: '0 6px 20px rgba(0,0,0,0.25)' }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

/* ══════════ 執職期間の登録モーダル ══════════ */
const DutyModal: React.FC<{
  ym: string; users: UserLite[]; positions: string[]; vesselId: string;
  onClose: () => void; onSaved: () => void; onError: (msg: string) => void;
}> = ({ ym, users, positions, vesselId, onClose, onSaved, onError }) => {
  const [userId, setUserId] = useState('');
  const [duty, setDuty] = useState('');
  const [start, setStart] = useState(`${ym}-01`);
  const [end, setEnd] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!userId || !duty.trim() || !start) { onError('乗組員・執職・開始日を入力してください'); return; }
    setSaving(true);
    try {
      await api.post('/boarding-sheet/duty-periods', {
        user_id: userId, vessel_id: vesselId || null, duty_position: duty.trim(),
        start_date: start, end_date: end || null, note: note || null,
      });
      onSaved();
    } catch (e: any) { onError(e?.message ?? '登録に失敗しました'); setSaving(false); }
  };

  const inp: React.CSSProperties = { border: '1.5px solid #DCE3EA', borderRadius: 9, padding: '9px 11px', fontSize: 13, outline: 'none', background: 'white', color: '#16283B', width: '100%', boxSizing: 'border-box' };
  const lbl: React.CSSProperties = { fontSize: 11.5, fontWeight: 700, color: '#5A6B7E', marginBottom: 5, display: 'block' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(16,28,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, width: 520, maxWidth: '94vw', padding: '22px 24px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: '#16283B' }}>執職期間を登録</span>
          <button onClick={onClose} style={{ marginLeft: 'auto', border: 'none', background: 'none', color: '#8395A7', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: 11.5, color: '#5A6B7E', lineHeight: 1.7 }}>
          辞令上の役職とは別に、実際に従事した職務（執職）の期間を登録します。<br />
          例）一等航海士の休暇中、二等航海士が「一等航海士」の職務を執る。デッキ↔エンジンの系統をまたぐ執職は登録しないでください。
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={lbl}>乗組員 *</label>
            <select value={userId} onChange={e => setUserId(e.target.value)} style={inp}>
              <option value="">— 選択 —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.user_name}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>執職（従事した役職）*</label>
            <input list="duty-pos-list" value={duty} onChange={e => setDuty(e.target.value)} placeholder="例: 一等航海士" style={inp} />
            <datalist id="duty-pos-list">{positions.map(p => <option key={p} value={p} />)}</datalist>
          </div>
          <div>
            <label style={lbl}>開始日 *</label>
            <input type="date" value={start} onChange={e => setStart(e.target.value)} style={inp} />
          </div>
          <div>
            <label style={lbl}>終了日（空欄=継続中）</label>
            <input type="date" value={end} onChange={e => setEnd(e.target.value)} style={inp} />
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>メモ</label>
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="例: 一等航海士の休暇代行" style={inp} />
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '11px', borderRadius: 10, border: '1.5px solid #DCE3EA', background: 'white', fontSize: 13, fontWeight: 700, color: '#5A6B7E', cursor: 'pointer' }}>キャンセル</button>
          <button onClick={save} disabled={saving}
            style={{ flex: 1, padding: '11px', borderRadius: 10, border: 'none', background: saving ? '#D8C29A' : '#B45309', color: 'white', fontSize: 13.5, fontWeight: 800, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? '登録中…' : '登録する'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══════════ イベント登録モーダル ══════════ */
const EventModal: React.FC<{
  ym: string; vesselId: string; users: UserLite[]; positions: string[];
  rests: Array<{ user_id: string; start_date: string; end_date: string; status: string }>;
  onClose: () => void; onSaved: (msg: string) => void; onError: (msg: string) => void;
}> = ({ ym, vesselId, users, positions, rests, onClose, onSaved, onError }) => {
  const [date, setDate] = useState(`${ym}-01`);
  const [endDate, setEndDate] = useState('');
  const [type, setType] = useState<'change' | 'berth' | 'dock' | 'other'>('change');
  const [port, setPort] = useState('');
  const [berth, setBerth] = useState('');
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [items, setItems] = useState<EventItem[]>([{ user_id: '', action: 'board', position: '', note: '' }]);
  const [saving, setSaving] = useState(false);

  const setItem = (i: number, patch: Partial<EventItem>) =>
    setItems(prev => prev.map((it, k) => (k === i ? { ...it, ...patch } : it)));

  /** 希望休との衝突（乗船なのに希望休が重なる人） */
  const conflicts = items
    .filter(it => it.user_id && it.action === 'board')
    .map(it => {
      const rest = rests.find(r => r.user_id === it.user_id && r.start_date <= date && r.end_date >= date);
      if (!rest) return null;
      const u = users.find(u => u.id === it.user_id);
      return `${u?.user_name ?? ''}さんは ${date} に希望休（${rest.status === 'approved' ? '承認済' : '申請中'}）があります`;
    })
    .filter(Boolean) as string[];

  const save = async () => {
    const valid = items.filter(it => it.user_id);
    setSaving(true);
    try {
      const r = await api.post<{ data: { applied: number; skipped: string[] } }>('/boarding-sheet/events', {
        vessel_id: vesselId, event_date: date, end_date: endDate || null,
        event_type: type, port_name: port, berth_note: berth, note, notify,
        items: valid.map(it => ({ user_id: it.user_id, action: it.action, position: it.position || null, note: it.note || null })),
      });
      const skipped = r.data?.skipped ?? [];
      onSaved(skipped.length ? `登録しました（スキップ: ${skipped.join(' / ')}）` : `イベントを登録し、発令予約 ${r.data?.applied ?? 0} 件を作成しました`);
    } catch (e: any) { onError(e?.message ?? '登録に失敗しました'); setSaving(false); }
  };

  const inp: React.CSSProperties = { border: '1.5px solid #DCE3EA', borderRadius: 9, padding: '9px 11px', fontSize: 13, outline: 'none', background: 'white', color: '#16283B' };
  const lbl: React.CSSProperties = { fontSize: 11.5, fontWeight: 700, color: '#5A6B7E', marginBottom: 5, display: 'block' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(16,28,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, width: 680, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto', padding: '22px 24px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 17, fontWeight: 800, color: '#16283B' }}>乗下船イベントを登録</span>
          <button onClick={onClose} style={{ marginLeft: 'auto', border: 'none', background: 'none', color: '#8395A7', cursor: 'pointer' }}><X size={20} /></button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={lbl}>日付 *</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ ...inp, width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={lbl}>終了日（停泊・ドック等）</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ ...inp, width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={lbl}>区分</label>
            <select value={type} onChange={e => setType(e.target.value as any)} style={{ ...inp, width: '100%', boxSizing: 'border-box' }}>
              <option value="change">交代（乗下船）</option>
              <option value="berth">仮バース・停泊</option>
              <option value="dock">ドック・入渠</option>
              <option value="other">その他</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={lbl}>港</label>
            <input value={port} onChange={e => setPort(e.target.value)} placeholder="例: 宇部" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={lbl}>バース・補足</label>
            <input value={berth} onChange={e => setBerth(e.target.value)} placeholder="例: 中山製鋼荷役バース" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} />
          </div>
        </div>

        {type === 'dock' && (
          <div style={{ background: '#FDF3E3', border: '1px solid #F2DCA6', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 12.5, color: '#92600A', fontWeight: 600 }}>
            🔧 ドック・入渠の期間中は運航していないため、配員基準のチェック対象外になります（乗下船表に🔧で表示）。
            工事に伴う乗下船があれば下の明細に登録できます（なくても構いません）。
          </div>
        )}
        <label style={lbl}>乗る人・降りる人</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
          {items.map((it, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 130px 1fr 28px', gap: 8, alignItems: 'center' }}>
              <select value={it.action} onChange={e => setItem(i, { action: e.target.value as any })}
                style={{ ...inp, padding: '8px 8px', fontWeight: 700, color: it.action === 'board' ? '#166534' : '#B42318' }}>
                <option value="board">▲ 乗船</option>
                <option value="disembark">▼ 下船</option>
              </select>
              <select value={it.user_id} onChange={e => setItem(i, { user_id: e.target.value })} style={{ ...inp, padding: '8px 8px' }}>
                <option value="">— 乗組員を選択 —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.user_name}</option>)}
              </select>
              <input list="pos-list" value={it.position ?? ''} onChange={e => setItem(i, { position: e.target.value })}
                placeholder="役職（乗船時）" disabled={it.action === 'disembark'}
                style={{ ...inp, padding: '8px 8px', background: it.action === 'disembark' ? '#F4F6F8' : 'white' }} />
              <input value={it.note ?? ''} onChange={e => setItem(i, { note: e.target.value })}
                placeholder="個別メモ（例: 宇部まで同乗）" style={{ ...inp, padding: '8px 8px' }} />
              <button onClick={() => setItems(prev => prev.filter((_, k) => k !== i))}
                style={{ border: 'none', background: 'none', color: '#C2CBD6', cursor: 'pointer' }}><X size={15} /></button>
            </div>
          ))}
        </div>
        <datalist id="pos-list">{positions.map(p => <option key={p} value={p} />)}</datalist>
        <button onClick={() => setItems(prev => [...prev, { user_id: '', action: 'board', position: '', note: '' }])}
          style={{ border: '1.5px dashed #C6D2DE', background: 'none', borderRadius: 9, padding: '7px 14px', fontSize: 12, fontWeight: 700, color: '#5A6B7E', cursor: 'pointer', marginBottom: 14 }}>
          ＋ 行を追加
        </button>

        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>メモ（従来の「※」書き）</label>
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="例: 平田船長宇部まで乗っていく／6:00 出港" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} />
        </div>

        {conflicts.length > 0 && (
          <div style={{ background: '#FEF6E7', border: '1px solid #F2DCA6', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
            {conflicts.map((c, i) => <div key={i} style={{ fontSize: 12.5, color: '#92600A', fontWeight: 600 }}>⚠ {c}</div>)}
          </div>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#2B3B4E', marginBottom: 16, cursor: 'pointer' }}>
          <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} />
          対象の乗組員へお知らせを配信する（発令通知）
        </label>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1.5px solid #DCE3EA', background: 'white', fontSize: 13.5, fontWeight: 700, color: '#5A6B7E', cursor: 'pointer' }}>キャンセル</button>
          <button onClick={save} disabled={saving || !date}
            style={{ flex: 2, padding: '12px', borderRadius: 10, border: 'none', background: saving ? '#9DB8D2' : 'linear-gradient(135deg,#0E5AA7,#2EA8D5)', color: 'white', fontSize: 14, fontWeight: 800, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? '登録中…' : '登録する（発令予約を作成）'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══════════ 設定モーダル（起算日＋配員基準） ══════════ */
const SettingsModal: React.FC<{
  yearStart: string; vesselId: string; vesselName: string; positions: string[];
  onClose: () => void; onSaved: (v: string) => void; onError: (msg: string) => void;
}> = ({ yearStart, vesselId, vesselName, positions, onClose, onSaved, onError }) => {
  const [mm, setMm] = useState(Number(yearStart.slice(0, 2)) || 1);
  const [dd, setDd] = useState(Number(yearStart.slice(3)) || 1);
  const [rules, setRules] = useState<ManningRule[]>([]);
  const [roleGroups, setRoleGroups] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get<{ data: { rules: Array<{ role_name: string; min_count: number }>; role_groups: string[] } }>(
          '/boarding-sheet/manning-rules', { vessel_id: vesselId });
        setRules((r.data?.rules ?? []).map(x => ({ role_name: x.role_name, min_count: x.min_count })));
        setRoleGroups(r.data?.role_groups ?? []);
      } catch { /* noop */ }
    })();
  }, [vesselId]);

  const save = async () => {
    setSaving(true);
    const v = `${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
    try {
      await api.put('/boarding-sheet/settings', { boarding_year_start: v });
      await api.put('/boarding-sheet/manning-rules', { vessel_id: vesselId, rules: rules.filter(r => r.role_name) });
      onSaved(v);
    } catch (e: any) { onError(e?.message ?? '保存に失敗しました'); setSaving(false); }
  };

  const selStyle: React.CSSProperties = { border: '1.5px solid #DCE3EA', borderRadius: 9, padding: '8px 10px', fontSize: 13, fontWeight: 700, background: 'white' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(16,28,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, width: 520, maxWidth: '94vw', maxHeight: '88vh', overflowY: 'auto', padding: '22px 24px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: '#16283B' }}>乗下船表の設定</span>
          <button onClick={onClose} style={{ marginLeft: 'auto', border: 'none', background: 'none', color: '#8395A7', cursor: 'pointer' }}><X size={18} /></button>
        </div>

        <div style={{ fontSize: 13, fontWeight: 800, color: '#0E5AA7', margin: '14px 0 6px' }}>① 累計日数の起算日（会社共通）</div>
        <p style={{ margin: '0 0 10px', fontSize: 12, color: '#5A6B7E', lineHeight: 1.7 }}>
          例）6月1日に設定 → 「累計」は毎年6/1〜翌5/31の合計でリセットされます。
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#2B3B4E' }}>毎年</span>
          <select value={mm} onChange={e => setMm(Number(e.target.value))} style={selStyle}>
            {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}月</option>)}
          </select>
          <select value={dd} onChange={e => setDd(Number(e.target.value))} style={selStyle}>
            {Array.from({ length: 28 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}日</option>)}
          </select>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#2B3B4E' }}>にリセット</span>
        </div>

        <div style={{ fontSize: 13, fontWeight: 800, color: '#0E5AA7', margin: '14px 0 6px' }}>② 配員基準（{vesselName}）</div>
        <p style={{ margin: '0 0 10px', fontSize: 12, color: '#5A6B7E', lineHeight: 1.7 }}>
          この船に必要な役職と最低人数です。下回る日は乗下船表に⚠が表示され、配乗シミュレーションでも不足アラートが出ます。
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
          {rules.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <select value={r.role_name} onChange={e => setRules(prev => prev.map((x, k) => k === i ? { ...x, role_name: e.target.value } : x))}
                style={{ ...selStyle, flex: 1 }}>
                <option value="">— 役職を選択 —</option>
                <optgroup label="役職グループ（同系統をまとめて数える）">
                  {roleGroups.map(g => <option key={g} value={g}>{g}</option>)}
                </optgroup>
                <optgroup label="役職マスタ（この役職だけを数える）">
                  {positions.filter(p => !roleGroups.includes(p)).map(p => <option key={p} value={p}>{p}</option>)}
                </optgroup>
              </select>
              <select value={r.min_count} onChange={e => setRules(prev => prev.map((x, k) => k === i ? { ...x, min_count: Number(e.target.value) } : x))}
                style={selStyle}>
                {Array.from({ length: 10 }, (_, k) => <option key={k + 1} value={k + 1}>{k + 1}名以上</option>)}
              </select>
              <button onClick={() => setRules(prev => prev.filter((_, k) => k !== i))}
                style={{ border: 'none', background: 'none', color: '#C2CBD6', cursor: 'pointer' }}><X size={15} /></button>
            </div>
          ))}
        </div>
        <button onClick={() => setRules(prev => [...prev, { role_name: '', min_count: 1 }])}
          style={{ border: '1.5px dashed #C6D2DE', background: 'none', borderRadius: 9, padding: '7px 14px', fontSize: 12, fontWeight: 700, color: '#5A6B7E', cursor: 'pointer', marginBottom: 18 }}>
          ＋ 基準を追加
        </button>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '11px', borderRadius: 10, border: '1.5px solid #DCE3EA', background: 'white', fontSize: 13, fontWeight: 700, color: '#5A6B7E', cursor: 'pointer' }}>キャンセル</button>
          <button onClick={save} disabled={saving}
            style={{ flex: 1, padding: '11px', borderRadius: 10, border: 'none', background: '#0E5AA7', color: 'white', fontSize: 13.5, fontWeight: 800, cursor: 'pointer' }}>
            {saving ? '保存中…' : '保存する'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CrewAssignPage;
